import { useState, useEffect } from "react";
import s from "../../../wizard.module.css";
import ModalCopropietario from "../../../Modals/ModalOwner";
import {
  Section,
  Field,
  TextField,
  SelectField,
  TextAreaField,
  CedulaField,
  ChipToggle,
  TipoPersonaSwitch,
  VehiculosField,
  PersonaCard,
  EmptyState,
} from "../../../FieldHelpers";
import { nombreDe, metaDe, inicialesDe } from "../../../persona";
import { useCedulaLookup } from "../../../../services/useCedulaLookup.js";
import { REGEX_EMAIL } from "../../../../services/validationRules";
import { useEsCliente } from "../../../../../../shared/hooks/useTenantTipo";
import { useLang } from "../../../../../../shared/i18n/useLang";

export default function Propietario({ formData, setFormData, errors = {}, section = "all" }) {
  const esCliente = useEsCliente();
  const L = useLang();
  const p = formData.propietario;

  // Un solo lugar para tocar el propietario
  const setProp = (cambios) =>
    setFormData((prev) => ({ ...prev, propietario: { ...prev.propietario, ...cambios } }));

  const handleChange = (e) => setProp({ [e.target.name]: e.target.value });
  const chg = (campo) => (e) => setProp({ [campo]: e.target.value });

  // ── Modo condominio: todo el propietario en un paso, en dos columnas ──────
  if (!esCliente) {
    return (
      <>
        <div className={s.columns}>
          <div className={s.column}>
            <Section
              title="Datos personales"
              subtitle="Identificación legal del titular y tipo de personería."
              action={
                <TipoPersonaSwitch
                  value={p.es_sociedad}
                  onChange={(v) => setProp({ es_sociedad: v })}
                />
              }
            >
              <OwnerIdentity p={p} setProp={setProp} handleChange={handleChange} errors={errors} />
            </Section>

            <Section
              title="Información de contacto"
              subtitle="Teléfonos y canales directos con la administración."
            >
              <OwnerPhones p={p} chg={chg} errors={errors} />
              <TextAreaField
                label="Notas"
                optional
                rows={3}
                placeholder="Observaciones sobre el propietario…"
                value={p.notas || ""}
                onChange={chg("notas")}
              />
            </Section>
          </div>

          <div className={s.column}>
            <Section
              title="Notificación y acceso"
              action={
                <ChipToggle
                  checked={!!p.sin_correo}
                  onChange={() => setProp({ sin_correo: !p.sin_correo })}
                  label="No tiene correo"
                  icon="fa-envelope"
                />
              }
            >
              <OwnerEmails p={p} setProp={setProp} handleChange={handleChange} errors={errors} />

              <Field label="Vehículo registrado">
                <VehiculosField
                  vehiculos={p.vehiculos || []}
                  errors={errors.vehiculos}
                  onChange={(vehiculos) => setProp({ vehiculos })}
                  textoAgregar={["Agregar vehículo", "Agregar otro vehículo"]}
                />
              </Field>
            </Section>

            <CoOwnerSection formData={formData} setFormData={setFormData} />
          </div>
        </div>
      </>
    );
  }

  // ── Modo cliente: el paso viene partido en identidad y contacto ───────────
  const showIdentidad = section === "all" || section === "identidad";
  const showContacto = section === "all" || section === "contacto";

  return (
    <>
      {showIdentidad && (
        <>
          <Section
            title={L("Identificación", "Identification")}
            subtitle={L(
              "Identificación legal del cliente tal como debe salir en la factura.",
              "Legal identification of the client as it must appear on the invoice.",
            )}
          >
            <SelectField
              name="tipo_cedula"
              label={L("Tipo de identificación", "ID type")}
              value={p.tipo_cedula || ""}
              onChange={handleChange}
            >
              <option value="">{L("- Seleccioná el tipo -", "- Select type -")}</option>
              <option value="FISICA">{L("Física", "Individual")}</option>
              <option value="JURIDICA">{L("Jurídica", "Company")}</option>
              <option value="DIMEX">DIMEX</option>
              <option value="NITE">NITE</option>
              <option value="PASAPORTE">{L("Pasaporte / Otro", "Passport / Other")}</option>
            </SelectField>

            <OwnerIdentity
              p={p}
              setProp={setProp}
              handleChange={handleChange}
              errors={errors}
              esCliente
            />
          </Section>

          <Section
            title={L("Dirección", "Address")}
            subtitle={L("Dirección fiscal del cliente.", "Client's billing address.")}
          >
            <div className={s.grid3}>
              <TextField
                label={L("Provincia", "Province")}
                placeholder="Ej: San José"
                value={p.direccion_provincia || ""}
                onChange={chg("direccion_provincia")}
              />
              <TextField
                label={L("Cantón", "Canton")}
                placeholder="Ej: Central"
                value={p.direccion_canton || ""}
                onChange={chg("direccion_canton")}
              />
              <TextField
                label={L("Distrito", "District")}
                placeholder="Ej: Carmen"
                value={p.direccion_distrito || ""}
                onChange={chg("direccion_distrito")}
              />
            </div>
            <TextAreaField
              label={L("Otras señas", "Other details")}
              rows={2}
              placeholder={L("Detalle de la dirección…", "Address details…")}
              value={p.direccion_senas || ""}
              onChange={chg("direccion_senas")}
            />
          </Section>
        </>
      )}

      {showContacto && (
        <>
          <Section
            title={L("Información de contacto", "Contact information")}
            subtitle={L("Teléfonos del cliente.", "Client phone numbers.")}
          >
            <OwnerPhones p={p} chg={chg} errors={errors} esCliente />
          </Section>

          <Section
            title={L("Correos electrónicos", "Emails")}
            subtitle={L(
              "A estas direcciones se envían facturas y recibos.",
              "Invoices and receipts are sent to these addresses.",
            )}
          >
            <OwnerEmails p={p} setProp={setProp} handleChange={handleChange} errors={errors} />
          </Section>
        </>
      )}
    </>
  );
}

// ── Identidad con búsqueda de cédula ────────────────────────────────────────
function OwnerIdentity({ p, setProp, handleChange, errors, esCliente = false }) {
  const { buscar, resultado, estado, reset } = useCedulaLookup();
  const [autoloaded, setAutoloaded] = useState(false);
  const L = useLang();
  const esJuridica = p.tipo_cedula === "JURIDICA";

  // Aplicar los datos del registro cuando llegan. Si la cédula es de una
  // sociedad, en modo condominio se pasa el switch a Sociedad.
  useEffect(() => {
    if (resultado && !autoloaded) {
      setProp({
        nombre: resultado.nombre || p.nombre,
        apellidos: resultado.apellidos || p.apellidos,
        ...(!esCliente && resultado.nombre_sociedad
          ? { es_sociedad: true, nombre_sociedad: resultado.nombre_sociedad }
          : {}),
      });
      setAutoloaded(true);
    }
    // setProp y p se recrean en cada render; el efecto solo debe correr cuando
    // llega un resultado nuevo de la búsqueda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultado, autoloaded, esCliente]);

  const soloRazonSocial = esCliente ? esJuridica : !!p.es_sociedad;

  return (
    <>
      <CedulaField
        label={esCliente ? L("Cédula / Identificación", "ID number") : "Cédula de Identidad"}
        value={p.cedula}
        onChange={(e) => {
          handleChange(e);
          setAutoloaded(false);
          reset();
        }}
        onBlur={(e) => buscar(e.target.value)}
        onBuscar={() => buscar(p.cedula)}
        estado={estado}
        error={errors.cedula}
      />

      {soloRazonSocial ? (
        <TextField
          name="nombre_sociedad"
          label={esCliente ? L("Nombre / Razón social", "Name / Company") : "Nombre de la sociedad"}
          required
          placeholder="Ej: Inversiones Sol S.A."
          value={p.nombre_sociedad || ""}
          onChange={handleChange}
          error={errors.nombre_sociedad || (esCliente ? errors.nombre : undefined)}
        />
      ) : (
        <div className={s.grid2}>
          <TextField
            name="nombre"
            label={L("Nombre", "First name")}
            required
            placeholder="Nombre del titular"
            value={p.nombre}
            onChange={handleChange}
            error={errors.nombre}
          />
          <TextField
            name="apellidos"
            label={L("Apellidos", "Last name")}
            required={esCliente}
            placeholder="Apellidos del titular"
            value={p.apellidos}
            onChange={handleChange}
            error={errors.apellidos}
          />
        </div>
      )}
    </>
  );
}

// ── Teléfonos ───────────────────────────────────────────────────────────────
function OwnerPhones({ p, chg, errors, esCliente = false }) {
  const L = useLang();
  return (
    <div className={s.grid3}>
      <TextField
        label={L("Celular", "Mobile")}
        optional
        placeholder="Ej: 8888-1234"
        value={p.telefono_cel || ""}
        onChange={chg("telefono_cel")}
        error={errors.telefono_cel}
      />
      <TextField
        label={L("Casa", "Home")}
        optional
        placeholder="Ej: 2222-1234"
        value={p.telefono_casa || ""}
        onChange={chg("telefono_casa")}
        error={errors.telefono_casa}
      />
      <TextField
        label={esCliente ? L("Otro teléfono", "Other phone") : L("Trabajo", "Work")}
        optional
        placeholder="Ej: 2200-1234"
        value={p.telefono_trabajo || ""}
        onChange={chg("telefono_trabajo")}
        error={errors.telefono_trabajo}
      />
    </div>
  );
}

// ── Correos: principal + adicionales ────────────────────────────────────────
function OwnerEmails({ p, setProp, handleChange, errors }) {
  const L = useLang();
  const extras = p.emailExtras || [];
  const correoOk = REGEX_EMAIL.test(String(p.emailPrincipal || "").trim());

  const setExtras = (lista) => setProp({ emailExtras: lista });

  if (p.sin_correo) {
    return (
      <EmptyState>
        <i className="fa-solid fa-envelope-circle-check" style={{ marginRight: 7, color: "var(--brand)" }} />
        {L(
          "Sin correo: a este propietario no se le enviarán facturas ni recibos por email.",
          "No email: this owner will not receive invoices or receipts by email.",
        )}
      </EmptyState>
    );
  }

  return (
    <>
      <Field
        label={L("Correo electrónico", "Email")}
        required
        hint={<span className={s.labelHintOk}>{L("Principal", "Primary")}</span>}
        error={errors.emailPrincipal}
      >
        <div className={s.inputGroup}>
          <div className={`${s.inputWrap} ${s.groupField}`}>
            <input
              name="emailPrincipal"
              type="email"
              className={[
                s.input,
                errors.emailPrincipal ? s.inputError : "",
                correoOk ? s.hasIcon : "",
              ].filter(Boolean).join(" ")}
              placeholder="email@ejemplo.com"
              value={p.emailPrincipal}
              onChange={handleChange}
            />
            {correoOk && (
              <span className={`${s.fieldIcon} ${s.iconOk}`}>
                <i className="fa-solid fa-circle-check" />
              </span>
            )}
          </div>
          <button
            type="button"
            className={`${s.groupBtn} ${s.groupBtnSoft}`}
            title={L("Agregar otro correo", "Add another email")}
            onClick={() => setExtras([...extras, { id: Date.now(), email: "" }])}
          >
            <i className="fa-solid fa-plus" />
          </button>
        </div>
      </Field>

      {extras.map((item, idx) => (
        <Field key={item.id} label={`${L("Correo adicional", "Extra email")} ${idx + 2}`} error={errors.emailExtras?.[idx]}>
          <div className={s.inputGroup}>
            <input
              type="email"
              className={errors.emailExtras?.[idx] ? `${s.input} ${s.inputError}` : s.input}
              placeholder="email@ejemplo.com"
              value={item.email}
              onChange={(e) =>
                setExtras(extras.map((x) => (x.id === item.id ? { ...x, email: e.target.value } : x)))
              }
            />
            <button
              type="button"
              className={`${s.groupBtn} ${s.groupBtnDanger}`}
              title={L("Quitar correo", "Remove email")}
              onClick={() => setExtras(extras.filter((x) => x.id !== item.id))}
            >
              <i className="fa-solid fa-trash" />
            </button>
          </div>
        </Field>
      ))}
    </>
  );
}

// ── Copropietarios ──────────────────────────────────────────────────────────
function CoOwnerSection({ formData, setFormData }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedIndex, setSelectedIndex] = useState(null);

  function abrirAgregar() {
    setMode("add");
    setSelectedIndex(null);
    setOpen(true);
  }

  function abrirEditar(id) {
    setMode("edit");
    setSelectedIndex(id);
    setOpen(true);
  }

  function quitar(id) {
    setFormData((prev) => ({
      ...prev,
      copropietarios: prev.copropietarios.filter((c) => c.id !== id),
    }));
  }

  return (
    <>
      <Section
        title="Copropietarios adicionales"
        action={
          <button type="button" className={`${s.btnPrimary} ${s.btnSmall}`} onClick={abrirAgregar}>
            <i className="fa-solid fa-plus" />
            Agregar
          </button>
        }
      >
        {formData.copropietarios.length === 0 ? (
          <EmptyState>Todavía no hay copropietarios. Los que agregues aparecen aquí.</EmptyState>
        ) : (
          <div className={s.personList}>
            {formData.copropietarios.map((co) => (
              <PersonaCard
                key={co.id}
                nombre={nombreDe(co)}
                avatar={inicialesDe(co)}
                meta={metaDe(co)}
                onEdit={() => abrirEditar(co.id)}
                onDelete={() => quitar(co.id)}
              />
            ))}
          </div>
        )}
      </Section>

      <ModalCopropietario
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        mode={mode}
        selectedIndex={selectedIndex}
      />
    </>
  );
}

